import { Tabs, Table, Cell, Pagination, LoadingFullScreen, Breadcrumb, Svg, Link, Checkbox } from 'components'
import { EmailMessageDialog } from 'pages/Emails/EmailMessageDialog'
import { getEmailMessages, cancelEmailMessage, getEmailMessage, deleteEmailMessage, markEmailMessageRead } from 'api/system'
import { emailCounts, refreshEmailCounts } from 'store/emailCounts'
import { DateTime } from 'luxon'
import { debounce } from 'utils/debounce'
import { timeUntilEmailSent } from 'utils'
import cancelIcon from 'assets/icons/outline/x-circle.svg'

const OUTBOUND = 0
const INBOUND = 1

export const Emails = ({ attrs: { tab } }) => {
  let emails
  let meta
  let activeTab
  const filters = { page: 1, search: null, statusId: 0, direction: OUTBOUND, sortorder: 'desc', hideCompleted: false }

  const debouncedSearch = debounce(val => {
    filters.search = val.length > 2 ? val : null
    refreshData()
  }, 300)

  const refreshData = () => {
    const params = { ...filters }
    if (params.statusId === null) {
      delete params.statusId
    }
    if (!params.hideCompleted || params.direction !== INBOUND) {
      delete params.hideCompleted
    }
    getEmailMessages(params).then(data => {
      emails = data.data
      meta = data.meta
    })
  }

  // Inbox holds captured replies. They are stored with a Sent status because no inbound one
  // exists, so they are separated by direction rather than by status.
  const tabs = [
    { name: 'Inbox', statusId: null, direction: INBOUND },
    { name: 'Queued', statusId: 0, direction: OUTBOUND },
    { name: 'Sent', statusId: 1, direction: OUTBOUND },
    { name: 'Failed', statusId: 2, direction: OUTBOUND }
  ].map((t, ix) => {
    return {
      id: ix + 1,
      name: t.name,
      statusId: t.statusId,
      direction: t.direction
    }
  })

  const selectTab = (name) => {
    tabs.forEach(t => {
      t.active = false
      if (t.name === name) {
        t.active = true
        activeTab = t
      }
    })
    filters.statusId = activeTab.statusId
    filters.direction = activeTab.direction
    filters.page = 1
    refreshData()
    var pageUrl = `?tab=${activeTab ? activeTab.name : ''}`
    window.history.pushState('', '', pageUrl)
  }

  // Inbox was called Received, so old links still land on it.
  if (!tab || tab === 'Received') {
    selectTab('Inbox')
  } else {
    selectTab(tab)
  }
  refreshEmailCounts()

  return {
    view () {
      const isReceivedTab = activeTab && activeTab.name === 'Inbox'
      tabs.forEach(t => {
        if (t.name === 'Inbox') {
          t.count = emailCounts().unread
          t.countClass = 'bg-indigo-100 text-indigo-700'
        } else if (t.name === 'Failed') {
          t.count = emailCounts().failed
          t.countClass = 'bg-red-100 text-red-700'
        }
      })
      return [
        m(Breadcrumb, {
          links: [{
            title: 'Emails'
          }]
        }),
        m('.mt-4.px-4.sm:px-6.lg:px-8', [
          m(Tabs, {
            tabs: tabs,
            onTabChange: (tabId) => {
              tabs.forEach(t => {
                if (t.id === tabId) {
                  selectTab(t.name)
                }
              })
            }
          }),
          isReceivedTab && m('.flex.justify-end', m(Checkbox, {
            value: 'hideCompleted',
            checked: filters.hideCompleted,
            label: 'Hide completed conversations',
            onchange: () => {
              filters.hideCompleted = !filters.hideCompleted
              filters.page = 1
              refreshData()
            }
          })),
          m('', { style: { maxHeight: 'calc(100vh)' } }, [
            m('div.shadow.h-full.border-b.border-gray-200.sm:rounded-lg',
              emails ? m(Table, {
                cols: [
                  {
                    label: isReceivedTab ? 'From' : 'To',
                    search: true,
                    searchPlaceholder: 'Search...',
                    searchOnChange: (val) => {
                      debouncedSearch(val)
                    }
                  },
                  {
                    label: 'Subject'
                  },
                  {
                    label: 'Source',
                    classes: ['w-20 justify-center']
                  },
                  {
                    label: 'Created',
                    classes: ['w-40 justify-center']
                  },
                  {
                    label: 'Delay',
                    classes: ['w-16 justify-center']
                  },
                  {
                    label: isReceivedTab ? 'Received' : 'Sent',
                    classes: ['w-40 justify-center']
                  },
                  {
                    label: '',
                    search: false,
                    classes: ['w-24 justify-center']
                  }
                ],
                filters: true
              },
              [
                m('.overflow-auto.flex-grow',
                  emails.length ? emails.map((v, ix) => {
                    return m(EmailListItem, { item: v, key: v.id, refreshData, tab: activeTab.name })
                  }) : m('p.text-center.p-3.text-gray-400', 'No results')
                ),
                meta && m(Pagination, {
                  meta: meta,
                  onpage: (page) => {
                    filters.page = page
                    refreshData()
                  }
                })
              ]) : m(LoadingFullScreen)
            )
          ])
        ])
      ]
    }
  }
}

const EmailListItem = ({ attrs: { item, classes = [], refreshData } }) => {
  let cancelEmailConfirm = false
  const refreshListAndCounts = () => {
    refreshData()
    refreshEmailCounts()
  }
  const cancelEmail = (emailMessageId) => {
    cancelEmailConfirm = false
    cancelEmailMessage(emailMessageId).then(refreshListAndCounts)
  }
  const deleteEmail = (emailMessageId) => {
    cancelEmailConfirm = false
    deleteEmailMessage(emailMessageId).then(refreshListAndCounts)
  }
  const markUnreadLocally = () => {
    item.readDate = null
    refreshEmailCounts()
  }
  const openEmail = (isUnread) => {
    currentEmail = {}
    getEmailMessage(item.id).then(d => {
      currentEmail = d
    })
    emailMessageOpen(true)
    if (isUnread) {
      markRead()
    }
  }
  const markRead = () => {
    markEmailMessageRead(item.id).then(() => {
      item.readDate = new Date().toISOString()
      refreshEmailCounts()
    })
  }
  const newFormat = { ...DateTime.DATETIME_SHORT, year: '2-digit' }
  const sched = flyd.stream('')

  let currentEmail = {}
  const emailMessageOpen = flyd.stream(false)
  let scheduleTimer
  // Counts down to the send, then refreshes once to pick up the sent date. A row already past
  // its delay when it appears has nothing to count, so it does not refresh.
  const calcScheduled = () => {
    sched(timeUntilEmailSent(item.createdDate, item.delay * 60))
    if (sched() !== '0s') {
      scheduleTimer = setTimeout(calcScheduled, 1000)
    } else if (scheduleTimer) {
      refreshData()
    }
    m.redraw()
  }
  if (item.direction !== INBOUND && !item.sentDate) {
    calcScheduled()
  }
  return {
    onremove () {
      clearTimeout(scheduleTimer)
    },
    view ({ attrs: { tab } }) {
      // A captured reply carries our own reply+token address in `to`, so it has to be labelled by
      // its sender — otherwise it reads as mail we sent ourselves.
      const isInbound = item.direction === INBOUND
      const isUnread = isInbound && !item.readDate
      const unreadClass = isUnread ? 'font-bold' : ''
      let dateCell
      if (isInbound) {
        dateCell = DateTime.fromISO(item.receivedDate).toLocaleString(newFormat)
      } else if (item.sentDate) {
        dateCell = DateTime.fromISO(item.sentDate).toLocaleString(newFormat)
      } else {
        dateCell = sched()
      }
      // Only an open reply needs chasing, so arrival urgency is flagged on those alone.
      let rowColor = 'bg-white'
      if (isInbound && !item.threadIsComplete && item.arrivalDate) {
        const daysUntilArrival = DateTime.fromISO(item.arrivalDate).diff(DateTime.now().startOf('day'), 'days').as('days')
        if (daysUntilArrival < 0) {
          rowColor = 'bg-red-200'
        } else if (daysUntilArrival <= 5) {
          rowColor = 'bg-orange-200'
        }
      }

      return m('div', {
        class: ['flex', 'flex-shrink-0', 'hover:bg-gray-300', 'focus:bg-gray-300', 'focus:outline-none', 'cursor-pointer', rowColor, ...classes].join(' '),
        role: 'button',
        tabindex: 0,
        style: {
          height: '40px'
        },
        onclick: () => openEmail(isUnread),
        onkeydown: e => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            openEmail(isUnread)
          }
        }
      }, [
        m(Cell,
          m('span', { class: unreadClass }, isInbound ? item.fromAddress : item.to)
        ),
        m(Cell, { cellClasses: ['tracking-tight'] }, [
          item.threadIsComplete && m('span.bg-green-100.text-green-800.rounded.px-1.mr-1.text-xs.font-normal', 'Completed'),
          item.assignedToName && m('span.bg-indigo-100.text-indigo-800.rounded.px-1.mr-1.text-xs.font-normal', item.assignedToName),
          m('span', { class: unreadClass }, item.subject)
        ]),
        m(Cell, { width: 'w-20', align: 'center', padding: 'py-2', noEllipsis: 'true' },
          item.source ? m(Link, {
            stopPropogation: true,
            href: item.bookingId ? `/bookings/${item.bookingId}` : item.meetingId ? `/meetings/${item.meetingId}` : item.invoiceId ? `/invoices/${item.invoiceId}` : null,
            classes: ['bg-gray-200', 'hover:bg-gray-100', 'text-black', 'rounded', 'relative', 'py-1', 'px-2', 'text-xs']
          }, item.source) : null
        ),
        m(Cell, { width: 'w-40', align: 'center' },
          DateTime.fromISO(item.createdDate).toLocaleString(newFormat)
        ),
        m(Cell, { width: 'w-16', align: 'center', padding: 'py-3', noEllipsis: 'true' },
          isInbound ? '' : item.delay
        ),
        m(Cell, { width: 'w-40', align: 'center', padding: 'px-2 py-3' },
          dateCell
        ),
        m(Cell, { width: 'w-24', align: 'right', padding: 'px-1 py-2', noEllipsis: true },
          // Nothing to cancel on a reply that has already arrived, and it has no sentDate, so
          // without this the cancel button would render on every inbound row.
          item.sentDate || isInbound ? '' : m('button.text-red-700.hover:text-red-900.text-xs.flex.items-center.ml-2.focus:bg-red-800.focus:text-red-200', {
            type: 'button',
            onclick (e) {
              if (cancelEmailConfirm) {
                if (tab !== 'Failed') {
                  cancelEmail(item.id)
                } else {
                  deleteEmail(item.id)
                }
              } else {
                cancelEmailConfirm = true
                setTimeout(() => {
                  cancelEmailConfirm = false
                  m.redraw()
                }, 3000)
              }
              e.cancelBubble = true
            }
          }, [
            m(Svg, { classes: ['w-6', 'h-6'] }, cancelIcon),
            cancelEmailConfirm && m('span.ml-1.px-2', 'Confirm')
          ]
          )
        ), emailMessageOpen() &&
          m(EmailMessageDialog, {
            open: emailMessageOpen,
            email: currentEmail,
            onChanged: refreshListAndCounts,
            onMarkedUnread: markUnreadLocally
          })
      ])
    }
  }
}
